// Game Configuration
export const CONFIG = {
    WORLD_SIZE: 1000,
    CAMERA_HEIGHT: 32,             // Increased height for better view
    CAMERA_DISTANCE: 45,           // Increased distance for wider view
    PLAYER_SPEED: 7,
    ENEMY_SPEED: 1.8,
    INITIAL_SPAWN_RATE: 3.5,
    MAX_ENEMIES: 300,
    ASCII_SIZE: 1.4,
    PLAYER_SIZE: 2.2,
    COLORS: {
        PLAYER: 0x00ff88,
        ENEMY: 0xff4444,           // Basic red corruption
        FAST_ENEMY: 0xff8800,      // Orange speed corruption
        TANK_ENEMY: 0x666666,      // Gray armored corruption
        ELITE_ENEMY: 0xffff00,     // Yellow elite corruption
        SPLITTER_ENEMY: 0xff66ff,  // Magenta splitting corruption
        RANGED_ENEMY: 0xff4488,    // Pink ranged corruption
        SWARM_ENEMY: 0xffaaaa,     // Light red swarm
        TELEPORTER_ENEMY: 0x8844ff, // Purple teleporter
        SHIELD_ENEMY: 0x4488ff,    // Blue shielded
        HEALER_ENEMY: 0x44ff44,    // Green healer
        BERSERKER_ENEMY: 0xff0000, // Bright red berserker
        SPAWNER_ENEMY: 0xffaa00,   // Orange spawner
        BOSS: 0xff00ff,            // Magenta major boss
        MINI_BOSS: 0xaa00aa,       // Dark magenta mini-boss
        
        // Special rare enemy colors
        PHANTOM_ENEMY: 0x888888,   // Gray phantom
        VIRUS_ENEMY: 0x00ff00,     // Green virus
        FIREWALL_ENEMY: 0x0088ff,  // Blue firewall
        MALWARE_ENEMY: 0xff8800,   // Orange malware
        
        EXPERIENCE: 0x4488ff,      // Blue experience
        BULLET: 0x88ffff,          // Cyan bullets
        ENEMY_BULLET: 0xff8888,    // Light red enemy bullets
        POWERUP: 0xffff44,         // Yellow powerups
        BACKGROUND: 0x002244       // Lighter background for better visibility
    }
};

// Character Data for Player Selection
export const CHARACTERS = {
    DEBUGGER: {
        id: 'DEBUGGER',
        name: 'The Debugger',
        char: '@',
        color: 0x00ff88,
        description: 'Balanced programmer with quick processing',
        stats: {
            healthMod: 1.0,
            speedMod: 1.0,
            expMod: 1.1
        },
        startingWeapon: 'ASCII_BEAM',
        ability: 'Quick Study: +10% experience gain'
    },
    HACKER: {
        id: 'HACKER',
        name: 'The Hacker',
        char: '#',
        color: 0xff4499,
        description: 'Fast but fragile penetration specialist',
        stats: {
            healthMod: 0.8,
            speedMod: 1.3,
            expMod: 1.0
        },
        startingWeapon: 'PIERCING_SHOT',
        ability: 'Deep Strike: All weapons gain +1 piercing'
    },
    GUARDIAN: {
        id: 'GUARDIAN',
        name: 'The Guardian',
        char: '■',
        color: 0x4488ff,
        description: 'Tanky defender with strong shields',
        stats: {
            healthMod: 1.5,
            speedMod: 0.7,
            expMod: 1.0
        },
        startingWeapon: 'ASCII_BEAM',
        ability: 'Fortified: 25% damage reduction'
    },
    ARCHITECT: {
        id: 'ARCHITECT',
        name: 'The Architect',
        char: '◆',
        color: 0xffaa00,
        description: 'Area damage specialist with explosive expertise',
        stats: {
            healthMod: 1.0,
            speedMod: 1.0,
            expMod: 1.0
        },
        startingWeapon: 'ROCKET',
        ability: 'Cascade: +50% explosion radius'
    },
    SPEEDRUNNER: {
        id: 'SPEEDRUNNER',
        name: 'The Speedrunner',
        char: '>',
        color: 0x88ffff,
        description: 'Ultra-fast glass cannon',
        stats: {
            healthMod: 0.6,
            speedMod: 1.6,
            expMod: 1.0
        },
        startingWeapon: 'RAPID_FIRE',
        ability: 'Overclock: No movement penalty while firing'
    },
    SYNTHESIZER: {
        id: 'SYNTHESIZER',
        name: 'The Synthesizer',
        char: '~',
        color: 0xaa88ff,
        description: 'Chain reaction specialist',
        stats: {
            healthMod: 0.9,
            speedMod: 1.1,
            expMod: 1.0
        },
        startingWeapon: 'CHAIN_LIGHTNING',
        ability: 'Resonance: Chain effects jump to more targets'
    }
};

// ASCII Character Definitions with Enhanced Enemy Art
export const ASCII_CHARS = {
    PLAYER: '@',
    ENEMY: 'ж',           // Basic corrupted data
    FAST_ENEMY: '⟩',      // Fast moving corruption
    TANK_ENEMY: '▣',      // Heavy armored corruption
    ELITE: '⚡',          // Elite corruption with energy
    SPLITTER: '✱',       // Splitting corruption
    RANGED: '◎',         // Ranged attacker with targeting
    SWARM: '◦',          // Small swarm unit
    TELEPORTER: '◈',     // Teleporting corruption
    SHIELD: '⬟',         // Shielded corruption
    HEALER: '✚',         // Healing corruption
    BERSERKER: '⚔',      // Berserker corruption
    SPAWNER: '⊕',        // Spawning corruption
    BOSS: '▓',           // Major boss corruption
    MINI_BOSS: '♦',      // Mini-boss corruption
    
    // Special rare enemies
    PHANTOM: '◊',        // Phasing corruption
    VIRUS: '※',         // Viral corruption
    FIREWALL: '▦',      // Defensive corruption
    MALWARE: '⚠',       // Dangerous corruption
    
    // Weapon projectiles
    BULLET: '•',
    PIERCING_BULLET: '◦',
    ROCKET: '♦',
    LASER: '|',
    CHAIN_LIGHTNING: '≋',
    ORBITAL: '○',
    MINE: '◉',
    ENEMY_BULLET: '°',
    
    // Pickups
    EXPERIENCE: '◊',
    HEALTH_PACK: '✚',
    POWERUP: '★'
};

// Special Boss ASCII Art (for future large boss implementations)
export const BOSS_ASCII_ART = {
    CORRUPTION_CORE: [
        '▓▓▓',
        '▓█▓',
        '▓▓▓'
    ],
    DATA_LEVIATHAN: [
        '◢██◣',
        '████',
        '◥██◤'
    ],
    GLITCH_OVERLORD: [
        '⚡▓⚡',
        '▓▓▓',
        '⚡▓⚡'
    ],
    SYSTEM_DESTROYER: [
        '▣▣▣▣▣',
        '▣███▣',
        '▣▣▣▣▣'
    ]
};

// Weapon Type Definitions
export const WEAPON_TYPES = {
    ASCII_BEAM: {
        name: 'ASCII Beam',
        damage: 25,
        fireRate: 1.5,
        range: 12,
        projectileSpeed: 14,
        char: ASCII_CHARS.BULLET,
        color: CONFIG.COLORS.BULLET
    },
    PIERCING_SHOT: {
        name: 'Piercing Code',
        damage: 18,
        fireRate: 1.0,
        range: 15,
        projectileSpeed: 18,
        piercing: 4,
        char: ASCII_CHARS.PIERCING_BULLET,
        color: 0x00ffff
    },
    RAPID_FIRE: {
        name: 'Syntax Burst',
        damage: 10,
        fireRate: 4.0,
        range: 10,
        projectileSpeed: 20,
        char: ASCII_CHARS.BULLET,
        color: 0xffaa00
    },
    ROCKET: {
        name: 'Logic Bomb',
        damage: 55,
        fireRate: 0.5,
        range: 18,
        projectileSpeed: 10,
        explosive: true,
        explosionRadius: 4,
        char: ASCII_CHARS.ROCKET,
        color: 0xff6600
    },
    LASER_BEAM: {
        name: 'Data Stream',
        damage: 35,
        fireRate: 0.8,
        range: 20,
        projectileSpeed: 25,
        char: ASCII_CHARS.LASER,
        color: 0x88ffaa
    },
    SPREAD_SHOT: {
        name: 'Multi-Thread',
        damage: 15,
        fireRate: 1.8,
        range: 10,
        projectileSpeed: 16,
        spreadCount: 3,
        char: ASCII_CHARS.BULLET,
        color: 0xaa88ff
    },
    CHAIN_LIGHTNING: {
        name: 'Chain Lightning',
        damage: 30,
        fireRate: 1.2,
        range: 15,
        projectileSpeed: 20,
        chainCount: 3,
        char: ASCII_CHARS.CHAIN_LIGHTNING,
        color: 0xffff00
    },
    ORBITAL: {
        name: 'Orbital Strike',
        damage: 40,
        fireRate: 2.0,
        range: 20,
        projectileSpeed: 12,
        orbital: true,
        char: ASCII_CHARS.ORBITAL,
        color: 0x00ffff
    },
    MINE_LAYER: {
        name: 'Mine Layer',
        damage: 45,
        fireRate: 0.8,
        range: 8,
        projectileSpeed: 5,
        explosive: true,
        explosionRadius: 3,
        mine: true,
        char: ASCII_CHARS.MINE,
        color: 0xff8800
    }
};

// Enhanced Upgrade Definitions with Weapon System
export const UPGRADES = [
    // Weapon upgrades - these will be dynamically generated based on available weapons
    ...Object.keys(WEAPON_TYPES).map(weaponKey => ({
        type: 'weapon',
        weaponType: weaponKey,
        name: `${WEAPON_TYPES[weaponKey].name}`,
        desc: (level) => {
            if (level === 0) return `Unlock ${WEAPON_TYPES[weaponKey].name}`;
            return `Upgrade ${WEAPON_TYPES[weaponKey].name} (Level ${level + 1})`;
        },
        effect: (player) => {
            player.addWeapon(weaponKey);
        }
    })),
    
    // General upgrades
    { 
        type: 'general',
        name: "Enhanced Targeting", 
        desc: "Increase all weapon damage by 15%", 
        effect: (player) => {
            // This will be handled in bullet creation
            player.damageMultiplier = (player.damageMultiplier || 1) * 1.15;
        }
    },
    { 
        type: 'general',
        name: "Auto-Repair Protocol", 
        desc: "Regenerate 4 health per second", 
        effect: (player) => player.regen = (player.regen || 0) + 4
    },
    { 
        type: 'general',
        name: "Speed Optimization", 
        desc: "Increase movement speed by 25%", 
        effect: (player) => player.speed *= 1.25
    },
    { 
        type: 'general',
        name: "Rapid Processing", 
        desc: "Increase all fire rates by 20%", 
        effect: (player) => {
            player.fireRateMultiplier = (player.fireRateMultiplier || 1) * 1.2;
        }
    },
    { 
        name: "Memory Expansion", 
        desc: "Increase maximum health by 40", 
        effect: (player, gameStats) => { 
            gameStats.maxHealth += 40; 
            gameStats.health += 40; 
        } 
    },
    { 
        name: "Data Collector", 
        desc: "Gain 60% more experience", 
        effect: (player) => player.expMultiplier *= 1.6
    },
    {
        name: "Extended Range",
        desc: "Increase weapon range by 50%",
        effect: () => {
            Object.keys(WEAPON_TYPES).forEach(key => {
                WEAPON_TYPES[key].range *= 1.5;
            });
        }
    },
    {
        name: "Critical Analysis",
        desc: "Increase critical hit chance by 5%",
        effect: (player) => player.critChance += 0.05
    },
    {
        name: "Piercing Code",
        desc: "Unlock Piercing Shot weapon",
        effect: (player) => {
            if (!player.weapons.includes('PIERCING_SHOT')) {
                player.weapons.push('PIERCING_SHOT');
                player.currentWeapon = 'PIERCING_SHOT';
            }
        }
    },
    {
        name: "Rapid Syntax",
        desc: "Unlock high-speed burst weapon",
        effect: (player) => {
            if (!player.weapons.includes('RAPID_FIRE')) {
                player.weapons.push('RAPID_FIRE');
                player.currentWeapon = 'RAPID_FIRE';
            }
        }
    },
    {
        name: "Explosive Algorithms",
        desc: "Unlock explosive rocket launcher",
        effect: (player) => {
            if (!player.weapons.includes('ROCKET')) {
                player.weapons.push('ROCKET');
                player.currentWeapon = 'ROCKET';
            }
        }
    },
    {
        name: "Data Stream Cannon",
        desc: "Unlock high-damage laser beam",
        effect: (player) => {
            if (!player.weapons.includes('LASER_BEAM')) {
                player.weapons.push('LASER_BEAM');
                player.currentWeapon = 'LASER_BEAM';
            }
        }
    },
    {
        name: "Multi-Threading",
        desc: "Unlock spread shot weapon",
        effect: (player) => {
            if (!player.weapons.includes('SPREAD_SHOT')) {
                player.weapons.push('SPREAD_SHOT');
                player.currentWeapon = 'SPREAD_SHOT';
            }
        }
    },
    {
        name: "Chain Reaction",
        desc: "Unlock chain lightning weapon",
        effect: (player) => {
            if (!player.weapons.includes('CHAIN_LIGHTNING')) {
                player.weapons.push('CHAIN_LIGHTNING');
                player.currentWeapon = 'CHAIN_LIGHTNING';
            }
        }
    },
    {
        name: "Orbital Artillery",
        desc: "Unlock orbital strike weapon",
        effect: (player) => {
            if (!player.weapons.includes('ORBITAL')) {
                player.weapons.push('ORBITAL');
                player.currentWeapon = 'ORBITAL';
            }
        }
    },
    {
        name: "Defensive Mining",
        desc: "Unlock mine layer weapon",
        effect: (player) => {
            if (!player.weapons.includes('MINE_LAYER')) {
                player.weapons.push('MINE_LAYER');
                player.currentWeapon = 'MINE_LAYER';
            }
        }
    },
    {
        name: "Fortified Code",
        desc: "Reduce all damage taken by 20%",
        effect: (player) => player.damageReduction = (player.damageReduction || 0) + 0.2
    },
    {
        name: "Magnetic Field",
        desc: "Increase experience pickup range by 100%",
        effect: (player) => player.magnetRange = (player.magnetRange || 1) * 2
    }
]; 